import type { DiagramSpec } from "../types"

/**
 * VidMax System Atlas — pillar-level map of every surface, gate, and provider.
 * Click any node to see its description and drill-down link in the side panel.
 *
 * Layers (top → bottom): consumer surfaces → API gateway → cross-cutting gates
 * → engine (services + jobs) → external providers.
 */
export const vidmaxSystemAtlas: DiagramSpec = {
  id: "vidmax-system-atlas",
  name: "VidMax System Atlas",
  description: "Pillar-level map of every surface, gate, and provider in VidMax",
  layers: [
    { id: "consumer", name: "Consumer Surfaces", color: "#6366f1" },
    { id: "api", name: "API Gateway", color: "#f59e0b" },
    { id: "gates", name: "Cross-Cutting Gates", color: "#a855f7" },
    { id: "engine", name: "Engine (Services + Jobs)", color: "#10b981" },
    { id: "providers", name: "External Providers", color: "#ec4899" },
  ],
  nodes: [
    // Consumer surfaces (rank 0)
    {
      id: "ugc-workspace",
      label: "UGC Workspace",
      layer: "consumer",
      rank: 0,
      type: "Pillar",
      description:
        "Image + asset canvas, template gallery, avatar/voice pickers. Drill: brain/vidmax/architecture/system-atlas-ugc-workspace.md",
      fields: { entry: "apps/vidmax-vite/src/components/UGCWorkspace/", drilldown: "system-atlas-ugc-workspace.md" },
    },
    {
      id: "video-creation",
      label: "Video Creation",
      layer: "consumer",
      rank: 0,
      type: "Pillar",
      description:
        "Storyboard, video editor, prompt builder, effect presets. Drill: TODO system-atlas-video-creation.md",
      fields: { entry: "apps/vidmax-vite/src/components/VideoEditor/" },
    },
    {
      id: "onboarding-paywall",
      label: "Onboarding & Paywall",
      layer: "consumer",
      rank: 0,
      type: "Pillar",
      description: "Auth, trial, Stripe checkout, subscription gating. Drill: TODO system-atlas-onboarding.md",
      fields: { entry: "apps/vidmax-vite/src/components/Auth/, Subscription/" },
    },
    {
      id: "admin-tools",
      label: "Admin & Test Bench",
      layer: "consumer",
      rank: 0,
      type: "Pillar",
      description:
        "Admin router, ugcTestBench for prompt iteration, pipeline review. Drill: TODO system-atlas-admin.md",
      fields: { entry: "apps/vidmax-api/src/router/admin.ts, ugcTestBench.ts" },
    },

    // API gateway (rank 1)
    {
      id: "trpc-routers",
      label: "tRPC Routers",
      layer: "api",
      rank: 1,
      type: "API",
      description: "~50 routers in apps/vidmax-api/src/router/_app.ts. Auth, validation, rate limiting wired here.",
      fields: { count: "~50 routers", entry: "apps/vidmax-api/src/router/_app.ts" },
    },
    {
      id: "public-api",
      label: "Public OpenAPI",
      layer: "api",
      rank: 1,
      type: "API",
      description: "OpenAPI exposure for selected routers (assets, info, series, team, video, voices). API-key auth.",
      fields: { files: "*.openapi.ts", entry: "apps/vidmax-api/src/router/" },
    },
    {
      id: "webhooks",
      label: "Provider Webhooks",
      layer: "api",
      rank: 1,
      type: "API",
      description: "Inbound webhooks from Replicate, Kie.ai, fal.ai, Stripe. Updates job state.",
      fields: { entry: "apps/vidmax-api/src/router/webhook.ts, qstash.ts" },
    },

    // Cross-cutting gates (rank 2) — every consumer hit passes through these
    {
      id: "auth-gate",
      label: "Auth & Permissions",
      layer: "gates",
      rank: 2,
      type: "Gate",
      description: "Supabase session, API-key auth, team/role checks. Applied via tRPC middleware.",
      fields: { entry: "apps/vidmax-api/src/middleware/, service/auth*" },
    },
    {
      id: "credits-gate",
      label: "Credit System",
      layer: "gates",
      rank: 2,
      type: "Gate",
      description:
        "Pricing config + debit/refund on generation. Drill: brain/vidmax/architecture/ugc-credits-and-pricing.md",
      fields: { entry: "apps/vidmax-api/src/service/credit.ts, pricing.ts", drilldown: "ugc-credits-and-pricing.md" },
    },
    {
      id: "rate-limit-gate",
      label: "Rate Limiting",
      layer: "gates",
      rank: 2,
      type: "Gate",
      description: "Per-user / per-route limits. Backed by Upstash Redis.",
      fields: { backend: "Upstash Redis", entry: "apps/vidmax-api/src/service/rateLimit*" },
    },

    // Engine (rank 3)
    {
      id: "image-engine",
      label: "Image Generation",
      layer: "engine",
      rank: 3,
      type: "Service",
      description: "image.ts service + handleImageGeneration Inngest job. Multi-provider routing.",
      fields: { entry: "apps/vidmax-api/src/service/image.ts" },
    },
    {
      id: "video-engine",
      label: "Video Generation",
      layer: "engine",
      rank: 3,
      type: "Service",
      description: "video/, videoEffects, videoTransformations, handleVideoEffect, handleImageAnimation jobs.",
      fields: { entry: "apps/vidmax-api/src/service/video/, videoEffects.ts" },
    },
    {
      id: "audio-engine",
      label: "Audio & Lipsync",
      layer: "engine",
      rank: 3,
      type: "Service",
      description: "TTS, voice cloning, lipsync, sound effects, music. Inngest handlers per modality.",
      fields: { entry: "apps/vidmax-api/src/service/audio/, voice.ts" },
    },
    {
      id: "workflow-engine",
      label: "Workflow Engine",
      layer: "engine",
      rank: 3,
      type: "Service",
      description: "Multi-step pipelines (image → animate → lipsync → caption). Drill: workflow-engine.md",
      fields: { entry: "apps/vidmax-api/src/service/workflow.ts", drilldown: "workflow-engine.md" },
    },
    // External providers (rank 4)
    {
      id: "llm-providers",
      label: "LLM Providers",
      layer: "providers",
      rank: 4,
      type: "External",
      description: "OpenAI, Anthropic, Gemini for prompt enhancement & narrative.",
      fields: { providers: "OpenAI, Anthropic, Gemini" },
    },
    {
      id: "video-providers",
      label: "Video Providers",
      layer: "providers",
      rank: 4,
      type: "External",
      description: "Kie.ai, Replicate, fal.ai, Veo, Kling, Sora.",
      fields: { providers: "Kie.ai, Replicate, fal.ai, Veo, Kling, Sora" },
    },
    {
      id: "audio-providers",
      label: "Audio Providers",
      layer: "providers",
      rank: 4,
      type: "External",
      description: "ElevenLabs, AWS Polly, Fish Audio, Deepgram.",
      fields: { providers: "ElevenLabs, Polly, Fish Audio, Deepgram" },
    },
    {
      id: "image-providers",
      label: "Image Providers",
      layer: "providers",
      rank: 4,
      type: "External",
      description: "Nano Banana Pro, Replicate (FLUX, etc.), Placid for templated graphics.",
      fields: { providers: "Nano Banana Pro, Replicate, Placid" },
    },
  ],
  edges: [
    // Consumer → API
    { id: "e-ugc-trpc", source: "ugc-workspace", target: "trpc-routers" },
    { id: "e-video-trpc", source: "video-creation", target: "trpc-routers" },
    { id: "e-onb-trpc", source: "onboarding-paywall", target: "trpc-routers" },
    { id: "e-admin-trpc", source: "admin-tools", target: "trpc-routers" },
    // API → Gates
    { id: "e-trpc-auth", source: "trpc-routers", target: "auth-gate" },
    { id: "e-trpc-credits", source: "trpc-routers", target: "credits-gate" },
    { id: "e-trpc-rate", source: "trpc-routers", target: "rate-limit-gate" },
    { id: "e-public-auth", source: "public-api", target: "auth-gate" },
    { id: "e-webhooks-engine", source: "webhooks", target: "image-engine", style: "dashed" },
    // Gates → Engine
    { id: "e-credits-image", source: "credits-gate", target: "image-engine" },
    { id: "e-credits-video", source: "credits-gate", target: "video-engine" },
    { id: "e-credits-audio", source: "credits-gate", target: "audio-engine" },
    { id: "e-credits-workflow", source: "credits-gate", target: "workflow-engine" },
    // Engine → Providers
    { id: "e-image-image", source: "image-engine", target: "image-providers" },
    { id: "e-video-video", source: "video-engine", target: "video-providers" },
    { id: "e-audio-audio", source: "audio-engine", target: "audio-providers" },
    { id: "e-workflow-llm", source: "workflow-engine", target: "llm-providers" },
  ],
  views: [
    { id: "full", name: "Full Atlas", layout: "TB" },
    { id: "consumer-flow", name: "Consumer Flow Only", layout: "TB", layers: ["consumer", "api", "gates", "engine"] },
    { id: "providers-only", name: "Engine ↔ Providers", layout: "TB", layers: ["engine", "providers"] },
  ],
  defaultView: "full",
  preferences: {
    nodeWidth: 200,
    rankSpacing: 130,
    nodeGap: 35,
    showLayerBands: true,
    edgeStyle: "smoothstep",
  },
}
